import type { BookType } from "@/schemas";

export type BookScopedTransactionType =
  | "buy"
  | "sell"
  | "exchange"
  | "currency_conversion"
  | "deposit"
  | "withdrawal"
  | "dividend";

export type BookAssetCategory = "stocks" | "crypto" | "cash";

export const DEFAULT_BOOK_TYPE: BookType = "general";

export const BOOK_TYPE_OPTIONS: Array<{
  value: BookType;
  label: string;
  description: string;
}> = [
  {
    value: "cash",
    label: "Cash account",
    description: "Cash only: deposits, withdrawals, and FX conversions.",
  },
  {
    value: "securities",
    label: "Securities (Stock, ETF)",
    description: "Stocks, ETFs, funds, and dividend workflows.",
  },
  {
    value: "crypto",
    label: "Crypto",
    description: "Crypto-focused trades and asset exchanges.",
  },
  {
    value: "general",
    label: "General",
    description: "Mixed portfolio for legacy or multi-asset setups.",
  },
];

export function getBookTypeLabel(bookType?: BookType): string {
  switch (bookType ?? DEFAULT_BOOK_TYPE) {
    case "cash":
      return "Cash account";
    case "securities":
      return "Securities (Stock, ETF)";
    case "crypto":
      return "Crypto";
    default:
      return "General";
  }
}

export function getBookTypeDotClass(bookType?: BookType): string {
  switch (bookType ?? DEFAULT_BOOK_TYPE) {
    case "cash":
      return "bg-emerald-500";
    case "securities":
      return "bg-blue-500";
    case "crypto":
      return "bg-amber-500";
    default:
      return "bg-slate-500";
  }
}

export function normalizeBookType(bookType?: BookType): BookType {
  return bookType ?? DEFAULT_BOOK_TYPE;
}

export function getDefaultAssetCategory(
  bookType?: BookType,
): BookAssetCategory {
  switch (normalizeBookType(bookType)) {
    case "cash":
      return "cash";
    case "crypto":
      return "crypto";
    default:
      return "stocks";
  }
}

export function getAllowedAssetCategories(
  bookType?: BookType,
): BookAssetCategory[] {
  switch (normalizeBookType(bookType)) {
    case "cash":
      return ["cash"];
    case "crypto":
      return ["crypto"];
    case "securities":
      return ["stocks"];
    default:
      return ["stocks", "crypto", "cash"];
  }
}

export function getAllowedTransactionTypes(
  bookType?: BookType,
): BookScopedTransactionType[] {
  switch (normalizeBookType(bookType)) {
    case "cash":
      return ["deposit", "withdrawal", "currency_conversion"];
    case "crypto":
      return ["buy", "sell", "exchange"];
    case "securities":
      return ["buy", "sell", "dividend"];
    default:
      return [
        "buy",
        "sell",
        "exchange",
        "currency_conversion",
        "deposit",
        "withdrawal",
        "dividend",
      ];
  }
}

export function shouldShowBookMode(bookType?: BookType): boolean {
  return normalizeBookType(bookType) !== "cash";
}
